import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { ProviderShell } from '../components/modules/ProviderShell';
import { ProviderErrorBoundary } from '../components/modules/ProviderErrorBoundary';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export function ModuleIframe({ workspace, getHealthStatus }) {
    const navigate = useNavigate();
    // Consume provider data from WorkspaceContext (single source of truth) instead
    // of calling useModuleInstallation here — avoids a duplicate API fetch + polling interval.
    const { providers, loading, moduleNavTarget } = useWorkspace();
    const { getAuthHeader } = useAuth();

    const installation = workspace.providerId
        ? providers.find(p => p.id === workspace.providerId || p.provider_id === workspace.providerId)
        : null;
    const Glyph = workspace.glyph;

    const requestInstall = useCallback(async () => {
        try {
            await axios.post(`${API}/feature-requests/`, {
                title: `Module install request: ${workspace.name}`,
                description: 'Requested from the runtime shell after visiting an uninstalled module workspace.',
                type: 'feature',
                category: 'other',
            }, { headers: getAuthHeader() });
            toast.success(`${workspace.name} install requested. Admins have been notified.`);
        } catch {
            toast.error(`Could not send request for ${workspace.name}. Please try again.`);
        }
    }, [workspace, getAuthHeader]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full min-h-[400px]">
                <div className="text-center">
                    <div className="w-8 h-8 border-[3px] rounded-full animate-spin mx-auto mb-3"
                         style={{ borderColor: 'var(--line)', borderTopColor: 'var(--ink-1)' }} />
                    <p className="text-ink-2 text-sm">Loading {workspace.name}...</p>
                </div>
            </div>
        );
    }

    if (!installation) {
        return (
            <div className="flex items-center justify-center h-full min-h-[400px]">
                <div className="text-center max-w-sm">
                    <div
                        className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center"
                        style={{ background: workspace.hue, color: '#fff' }}
                    >
                        {Glyph ? <Glyph size={26} strokeWidth={2} /> : null}
                    </div>
                    <h2 className="text-lg font-semibold text-ink-1 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {workspace.name} is not installed
                    </h2>
                    <p className="text-sm text-ink-2 mb-4">
                        Contact your administrator to enable this module provider.
                    </p>
                    <button
                        onClick={requestInstall}
                        className="px-4 py-2 text-sm font-medium rounded-lg"
                        style={{ background: 'var(--ink-1)', color: 'var(--canvas)' }}
                    >
                        Request install
                    </button>
                </div>
            </div>
        );
    }

    return (
        <ProviderErrorBoundary
            providerId={workspace.providerId}
            providerName={workspace.name}
        >
            <ProviderShell
                providers={[{
                    id: installation.id || installation.provider_id,
                    name: installation.name || workspace.name,
                    url: installation.url || installation.iframe_url,
                    icon: workspace.glyph,
                }]}
                activeProviderId={installation.id || installation.provider_id}
                onNavigate={(path) => navigate(path)}
                navigateToPath={moduleNavTarget?.path || null}
                navigateToNonce={moduleNavTarget?.nonce || 0}
            />
        </ProviderErrorBoundary>
    );
}

export default ModuleIframe;
